import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Map, Bookmark, User, Store } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BottomNav = () => {
  const { isMerchant } = useAuth();
  const { pathname }   = useLocation();

  const leftItems  = [
    { to: '/',          icon: Home,     label: 'Accueil' },
    { to: '/map',       icon: Map,      label: 'Carte'   },
  ];
  const rightItems = [
    { to: '/favorites', icon: Bookmark, label: 'Favoris' },
    { to: '/profile',   icon: User,     label: 'Profil'  },
  ];
  const guestItems = [...leftItems, ...rightItems];
  const isMerchantActive = pathname.startsWith('/merchant');

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      padding: '0 0.75rem 0.75rem',
      pointerEvents: 'none',
      zIndex: 1000,
    }}>
      <nav 
        className="bottom-nav" 
        style={{ 
          pointerEvents: 'auto',
          width: '100%',
          padding: '0.5rem 0.25rem',
          borderRadius: 'var(--r-xl, 20px)',
          background: 'var(--nav-bg, rgba(var(--bg-rgb), 0.9))',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: `
            0 -4px 24px rgba(0,0,0,0.08),
            0 8px 32px rgba(0,0,0,0.12),
            inset 0 1px 0 rgba(255,255,255,0.1)
          `,
          border: '1px solid rgba(255,255,255,0.06)',
        }} 
        role="navigation" 
        aria-label="Navigation principale"
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}>
          {isMerchant ? (
            <>
              {/* Left items - prennent l'espace disponible */}
              <div style={{ 
                display: 'flex', 
                flex: 1, 
                justifyContent: 'space-around',
                gap: '0.25rem',
              }}>
                {leftItems.map(item => <NavPill key={item.to} item={item} />)}
              </div>
              
              {/* FAB Merchant - fixe au centre */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem',
                marginTop: '-1.25rem',
                padding: '0 0.5rem',
                flexShrink: 0,
              }}>
                <NavLink
                  to="/merchant"
                  aria-label="Mon magasin"
                >
                  {({ isActive }) => (
                    <div style={{
                      width: 58,
                      height: 58,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isActive || isMerchantActive
                        ? 'linear-gradient(145deg, #00d9b4, #009e85)'
                        : 'var(--bg)',
                      color: isActive || isMerchantActive ? '#fff' : 'var(--text-2)',
                      boxShadow: isActive || isMerchantActive
                        ? '0 8px 28px rgba(0,158,133,.5), inset 0 1px 0 rgba(255,255,255,0.25)'
                        : '0 4px 16px rgba(0,0,0,0.12), var(--neu-sm)',
                      transform: isActive || isMerchantActive ? 'translateY(-6px) scale(1.05)' : 'translateY(0)',
                      transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}>
                      <Store 
                        size={26} 
                        strokeWidth={isActive || isMerchantActive ? 2.5 : 2}
                      />
                    </div>
                  )}
                </NavLink>
                <span style={{
                  fontSize: '0.625rem',
                  fontWeight: isMerchantActive ? 700 : 600,
                  color: isMerchantActive ? 'var(--primary)' : 'var(--text-3)',
                  letterSpacing: '0.2px',
                }}>
                  Magasin
                </span>
              </div>
              
              {/* Right items - prennent l'espace disponible */}
              <div style={{ 
                display: 'flex', 
                flex: 1, 
                justifyContent: 'space-around',
                gap: '0.25rem',
              }}>
                {rightItems.map(item => <NavPill key={item.to} item={item} />)}
              </div>
            </>
          ) : (
            /* Mode invité - 4 items répartis équitablement */
            <div style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around',
            }}>
              {guestItems.map(item => <NavPill key={item.to} item={item} />)}
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

const NavPill = ({ item }) => {
  const { pathname } = useLocation();
  const isActive = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
  
  return (
    <NavLink 
      to={item.to} 
      end={item.to === '/'} 
      style={{ 
        textDecoration: 'none',
        flex: 1,
      }}
      aria-label={item.label}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.25rem',
        padding: '0.375rem 0.25rem',
        borderRadius: 'var(--r-lg, 14px)',
        background: isActive ? 'var(--nav-active-bg, rgba(var(--primary-rgb), 0.12))' : 'transparent',
        transition: 'all 0.2s ease',
        position: 'relative',
        minWidth: 64,
      }}>
        {/* Indicateur actif - dot */}
        {isActive && (
          <div style={{
            position: 'absolute',
            top: 4,
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: 'var(--primary)',
            boxShadow: '0 0 10px var(--primary)',
            animation: 'pulse 2s infinite',
          }}/>
        )}
        
        <item.icon 
          size={isActive ? 23 : 25} 
          strokeWidth={isActive ? 2.5 : 2}
          color={isActive ? 'var(--primary)' : 'var(--text-2)'}
          style={{
            transition: 'all 0.2s ease',
            transform: isActive ? 'scale(0.92)' : 'scale(1)',
            filter: isActive ? 'drop-shadow(0 2px 6px rgba(var(--primary-rgb), 0.35))' : 'none',
          }}
        />
        
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: isActive ? 700 : 500,
          color: isActive ? 'var(--primary)' : 'var(--text-3)',
          letterSpacing: isActive ? '-0.2px' : '0',
          transition: 'all 0.15s ease',
        }}>
          {item.label}
        </span>
      </div>
    </NavLink>
  );
};

export default BottomNav;